import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import LangToggle from "@/components/LangToggle";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2 } from "lucide-react";

const BG = "https://static.prod-images.emergentagent.com/jobs/44e922b4-6326-4e9e-a61b-a036e60b36dc/images/2443dbeee6be45b43207c5d6558575502d486232fa3e47d5b502746996b3d4d4.png";

export default function Login() {
  const { login } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error(t("login.error.empty"));
      return;
    }
    setLoading(true);
    try {
      const res = await login(code.trim());
      toast.success(`${t("login.welcome")}, ${res.code_label}`);
      nav("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || t("login.error.invalid");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-black/95" />
      </div>
      <div className="relative grain min-h-screen flex flex-col">
        <div className="px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="login-back" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("common.back_home")}
          </Link>
          <LangToggle testid="login-lang-toggle" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <div className="fade-up backdrop-blur-xl bg-white/[0.04] border border-white/10 p-10 md:p-12">
              <div className="flex items-center gap-3 mb-10">
                <div className="h-10 w-10 flex items-center justify-center border border-[#D4AF37]/70 text-[#D4AF37] font-serif italic">
                  A
                </div>
                <div className="leading-none">
                  <div className="font-serif text-base">AURUM Academy</div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA] mt-1">{t("login.access_label")}</div>
                </div>
              </div>

              <h1 className="font-serif text-3xl md:text-4xl font-light leading-tight">
                {t("login.title1")} <em className="italic text-[#D4AF37]">{t("login.title2")}</em>.
              </h1>
              <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">
                {t("login.subtitle")}
              </p>

              <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="login-form">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.32em] text-[#A1A1AA]">{t("login.label.code")}</span>
                  <div className="mt-3 relative">
                    <KeyRound className="h-4 w-4 text-[#A1A1AA] absolute left-4 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="login-code-input"
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder={t("login.placeholder")}
                      className="w-full bg-black/50 border border-white/15 pl-11 pr-4 py-4 text-sm tracking-[0.12em] uppercase text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37] transition-colors"
                    />
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.22em] hover:bg-[#F5F5DC] transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {loading ? t("login.submit.loading") : t("login.submit")}
                </button>
              </form>

              <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-[#52525B]">
                {t("login.footer.note")}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-10 py-6 text-xs text-[#52525B] uppercase tracking-[0.28em] text-center">
          © 2026 AURUM Academy
        </div>
      </div>
    </div>
  );
}
